import { useState } from 'react';
import { api, openDocument } from '../../lib/api';
import { hasRole } from '../../lib/auth';
import { T, useGym } from '../../lib/gym';
import { useApi } from '../../lib/hooks';
import { useI18n } from '../../lib/i18n';
import { Link, navigate, setQuery, useLocation } from '../../lib/router';
import { AsyncButton, Badge, Button, Card, Checkbox, DataTable, ErrorBox, Field, Input, Loading, Modal, PageHeader, PersonPicker, Select, Tabs, useToast } from '../../ui/components';
import { ResourcePage, SchemaForm, useMeta } from '../../ui/schema';
import { AvailabilityEditor, EarningsView } from '../trainer/trainer';
import { ContractGenerator, ContractsTable } from './crm';


const EMPLOYMENT = ['freelancer', 'employee_permanent', 'employee_fixed_term', 'on_call', 'intern', 'volunteer', 'agency', 'payroll', 'director', 'other'];
const ROLES = ['owner', 'manager', 'desk', 'finance', 'trainer'];

export function TrainersPage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const list = useApi(() => api<any[]>(`${T(gym.slug)}/admin/trainers`), []);
  const [adding, setAdding] = useState(false);
  const [v, setV] = useState({ firstName: '', lastName: '', email: '', phone: '', invite: true, employment_type: 'freelancer', hourly: '', company_name: '', company_registration: '', vat_number: '', iban: '' });
  const mgmt = hasRole(gym.membership, 'owner', 'manager');
  return (
    <div className="page">
      <PageHeader title={i18n.t('nav.trainers')} actions={mgmt && <Button onClick={() => setAdding(true)}>+ {i18n.t('team.add_trainer')}</Button>} />
      {list.error ? <ErrorBox error={list.error} /> : !list.data ? <Loading /> : (
        <DataTable rows={list.data} onRowClick={(t) => navigate(`${gym.base}/admin/trainers/${t.person_id}`)} columns={[
          { key: 'name', label: i18n.field('name'), render: (t) => `${t.first_name} ${t.last_name}` },
          { key: 'employment_type', label: i18n.field('employment_type'), render: (t) => i18n.option(t.employment_type) },
          { key: 'hourly_rate_cents', label: i18n.field('hourly_rate_cents'), render: (t) => (t.hourly_rate_cents ? i18n.money(t.hourly_rate_cents) : '—') },
          { key: 'email', label: i18n.field('email') },
          { key: 'phone', label: i18n.field('phone') },
          { key: 'status', label: i18n.field('status'), render: (t) => <Badge value={t.status} /> },
        ]} empty={i18n.t('team.no_trainers')} />
      )}
      {adding && (
        <Modal wide title={i18n.t('team.add_trainer')} onClose={() => setAdding(false)} footer={
          <AsyncButton disabled={!v.firstName || !v.lastName} onClick={async () => {
            const r = await api(`${T(gym.slug)}/admin/trainers`, {
              body: {
                firstName: v.firstName, lastName: v.lastName, email: v.email || undefined, phone: v.phone || undefined, invite: v.invite,
                profile: {
                  employment_type: v.employment_type, hourly_rate_cents: v.hourly ? Math.round(Number(v.hourly) * 100) : null,
                  company_name: v.company_name || null, company_registration: v.company_registration || null, vat_number: v.vat_number || null, iban: v.iban || undefined,
                },
              },
            });
            toast.ok(i18n.t(v.invite && v.email ? 'team.trainer_invited' : 'team.trainer_added'));
            setAdding(false);
            navigate(`${gym.base}/admin/trainers/${r.person.id}`);
          }}>{i18n.t('common.create')}</AsyncButton>
        }>
          <div className="grid-2">
            <Field label={`${i18n.field('first_name')} *`}><Input value={v.firstName} onChange={(e) => setV({ ...v, firstName: e.target.value })} /></Field>
            <Field label={`${i18n.field('last_name')} *`}><Input value={v.lastName} onChange={(e) => setV({ ...v, lastName: e.target.value })} /></Field>
            <Field label={i18n.field('email')}><Input type="email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} /></Field>
            <Field label={i18n.field('phone')}><Input type="tel" value={v.phone} onChange={(e) => setV({ ...v, phone: e.target.value })} /></Field>
            <Field label={i18n.field('employment_type')}><Select value={v.employment_type} onChange={(e) => setV({ ...v, employment_type: e.target.value })} options={EMPLOYMENT.map((x) => ({ value: x, label: i18n.option(x) }))} /></Field>
            <Field label={i18n.field('hourly_rate_cents')}><Input type="number" step="0.01" min={0} value={v.hourly} onChange={(e) => setV({ ...v, hourly: e.target.value })} /></Field>
            {['freelancer', 'agency', 'director'].includes(v.employment_type) && <>
              <Field label={i18n.field('company_name')}><Input value={v.company_name} onChange={(e) => setV({ ...v, company_name: e.target.value })} /></Field>
              <Field label={i18n.field('company_registration')} hint={i18n.t('team.kvk_hint')}><Input value={v.company_registration} onChange={(e) => setV({ ...v, company_registration: e.target.value })} /></Field>
              <Field label={i18n.field('vat_number')}><Input value={v.vat_number} onChange={(e) => setV({ ...v, vat_number: e.target.value })} /></Field>
            </>}
            <Field label={i18n.field('iban')}><Input autoComplete="off" value={v.iban} onChange={(e) => setV({ ...v, iban: e.target.value })} /></Field>
          </div>
          <Checkbox checked={v.invite} onChange={(c) => setV({ ...v, invite: c })} label={i18n.t('team.send_invite')} hint={i18n.t('team.send_invite_hint')} />
          {v.employment_type === 'freelancer' && <p className="small muted">{i18n.t('team.freelancer_note')}</p>}
        </Modal>
      )}
    </div>
  );
}

export function TrainerDetailPage({ personId }: { personId: string }) {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const { query } = useLocation();
  const tab = query.get('tab') ?? 'profile';
  const meta = useMeta(gym.slug, 'trainer_profiles');
  const profile = useApi(async () => (await api<{ items: any[] }>(`${T(gym.slug)}/admin/r/trainer_profiles`, { query: { person_id: personId } })).items[0] ?? null, [personId]);
  const person = useApi(() => api(`${T(gym.slug)}/admin/r/persons/${personId}`), [personId]);
  const contracts = useApi(async () => (await api<{ items: any[] }>(`${T(gym.slug)}/admin/r/contracts`, { query: { person_id: personId } })).items, [personId]);
  const money = hasRole(gym.membership, 'owner', 'manager', 'finance');
  const [period, setPeriod] = useState(new Date(Date.now() - 20 * 86_400_000).toISOString().slice(0, 7));
  if (profile.error) return <div className="page"><ErrorBox error={profile.error} /></div>;
  if (!profile.data && profile.loading) return <Loading />;
  const p = person.data;
  return (
    <div className="page">
      <Link to={`${gym.base}/admin/trainers`} className="small">← {i18n.t('nav.trainers')}</Link>
      <PageHeader title={p ? `${p.first_name} ${p.last_name}` : '…'} subtitle={[p?.email, p?.phone].filter(Boolean).join(' · ')}
        actions={<Button variant="secondary" onClick={() => navigate(`${gym.base}/admin/planner?trainer=${personId}`)}>{i18n.t('team.view_schedule')}</Button>} />
      <Tabs value={tab} onChange={(v) => setQuery({ tab: v })} tabs={[
        { id: 'profile', label: i18n.t('team.tab_profile') },
        { id: 'availability', label: i18n.t('nav.availability') },
        { id: 'documents', label: i18n.t('trainer.documents') },
        { id: 'contracts', label: i18n.t('nav.contracts') },
        { id: 'earnings', label: i18n.t('nav.earnings'), hidden: !money },
        { id: 'statements', label: i18n.t('team.statements'), hidden: !money },
      ]} />
      {tab === 'profile' && (meta && profile.data ? <Card><SchemaForm slug={gym.slug} meta={meta} row={profile.data} hide={['person_id']} onSaved={() => { profile.reload(); toast.ok(i18n.t('common.saved')); }} /></Card> : <Loading />)}
      {tab === 'availability' && <AvailabilityEditor personId={personId} adminMode />}
      {tab === 'documents' && <ResourcePage slug={gym.slug} resource="trainer_documents" query={{ person_id: personId }} />}
      {tab === 'contracts' && (
        <div className="stack">
          <Card>{contracts.data ? <ContractsTable rows={contracts.data} reload={contracts.reload} /> : <Loading />}</Card>
          <Card title={i18n.t('contracts.new')}><ContractGenerator personId={personId} kind="trainer" onCreated={contracts.reload} /></Card>
        </div>
      )}
      {tab === 'earnings' && <EarningsView path={`${T(gym.slug)}/admin/trainers/${personId}/earnings`} />}
      {tab === 'statements' && (
        <div className="stack">
          <Card title={i18n.t('team.generate_statements')}>
            <p className="small muted">{i18n.t('team.statements_help')}</p>
            <div className="row gap-sm">
              <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
              <AsyncButton onClick={async () => { const r = await api(`${T(gym.slug)}/admin/trainer-statements/generate`, { body: { period } }); toast.ok(i18n.t('team.statements_generated', { count: r.length ?? r.count ?? 0 })); }}>{i18n.t('team.generate')}</AsyncButton>
            </div>
          </Card>
          <ResourcePage slug={gym.slug} resource="trainer_statements" query={{ person_id: personId }} searchable={false} />
        </div>
      )}
    </div>
  );
}

export function StaffPage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const list = useApi(() => api<any[]>(`${T(gym.slug)}/admin/staff`), []);
  const [edit, setEdit] = useState<any | null>(null);
  const [invite, setInvite] = useState<{ email: string; firstName: string; lastName: string; roles: string[]; locationIds: string[] } | null>(null);
  const isOwner = hasRole(gym.membership, 'owner');
  const roleBoxes = (roles: string[], set: (r: string[]) => void) => (
    <div className="stack-sm">
      {ROLES.filter((r) => r !== 'owner' || isOwner).map((r) => (
        <Checkbox key={r} checked={roles.includes(r)} onChange={(c) => set(c ? [...roles, r] : roles.filter((x) => x !== r))} label={i18n.option(r)} hint={i18n.t(`team.role_${r}`)} />
      ))}
    </div>
  );
  const locBoxes = (ids: string[], set: (ids: string[]) => void) => (gym.locations.length > 1 ? (
    <Field label={i18n.t('team.locations')} hint={i18n.t('form.empty_means_all')}>
      {gym.locations.map((l) => <Checkbox key={l.id} checked={ids.includes(l.id)} onChange={(c) => set(c ? [...ids, l.id] : ids.filter((x) => x !== l.id))} label={l.name} />)}
    </Field>
  ) : null);
  return (
    <div className="page">
      <PageHeader title={i18n.t('nav.staff')} actions={<Button onClick={() => setInvite({ email: '', firstName: '', lastName: '', roles: ['desk'], locationIds: [] })}>+ {i18n.t('team.invite_staff')}</Button>} />
      {!list.data ? <Loading /> : (
        <DataTable rows={list.data} onRowClick={(s) => setEdit({ ...s, locationIds: s.location_ids ?? [] })} columns={[
          { key: 'name', label: i18n.field('name'), render: (s) => s.name ?? s.email },
          { key: 'email', label: i18n.field('email') },
          { key: 'roles', label: i18n.t('account.roles'), render: (s) => <span className="row gap-sm wrap">{(s.roles ?? []).filter((r: string) => r !== 'client').map((r: string) => <span key={r} className="badge badge-neutral">{i18n.option(r)}</span>)}</span> },
          { key: 'totp_enabled', label: '2FA', render: (s) => (s.totp_enabled ? '✓' : '—') },
          { key: 'last_login_at', label: i18n.t('team.last_login'), render: (s) => (s.last_login_at ? i18n.dateTime(s.last_login_at) : '—') },
          { key: 'status', label: i18n.field('status'), render: (s) => <Badge value={s.status} /> },
        ]} />
      )}
      {edit && (
        <Modal title={edit.name ?? edit.email} onClose={() => setEdit(null)} footer={
          <AsyncButton onClick={async () => {
            await api(`${T(gym.slug)}/admin/staff/${edit.id}`, { method: 'PATCH', body: { roles: edit.roles, status: edit.status, locationIds: edit.locationIds.length ? edit.locationIds : null } });
            toast.ok(i18n.t('common.saved'));
            setEdit(null);
            list.reload();
          }}>{i18n.t('common.save')}</AsyncButton>
        }>
          {roleBoxes(edit.roles, (roles) => setEdit({ ...edit, roles }))}
          {locBoxes(edit.locationIds, (locationIds) => setEdit({ ...edit, locationIds }))}
          <Field label={i18n.field('status')}><Select value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} options={['active', 'suspended'].map((s) => ({ value: s, label: i18n.option(s) }))} /></Field>
        </Modal>
      )}
      {invite && (
        <Modal title={i18n.t('team.invite_staff')} onClose={() => setInvite(null)} footer={
          <AsyncButton disabled={!invite.email || !invite.firstName || !invite.roles.length} onClick={async () => {
            await api(`${T(gym.slug)}/admin/staff`, { body: { ...invite, locationIds: invite.locationIds.length ? invite.locationIds : null } });
            toast.ok(i18n.t('crm.invite_sent'));
            setInvite(null);
            list.reload();
          }}>{i18n.t('team.send_invite')}</AsyncButton>
        }>
          <div className="grid-2">
            <Field label={i18n.field('first_name')}><Input value={invite.firstName} onChange={(e) => setInvite({ ...invite, firstName: e.target.value })} /></Field>
            <Field label={i18n.field('last_name')}><Input value={invite.lastName} onChange={(e) => setInvite({ ...invite, lastName: e.target.value })} /></Field>
            <Field label={i18n.field('email')} className="span-2"><Input type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} /></Field>
          </div>
          {roleBoxes(invite.roles, (roles) => setInvite({ ...invite, roles }))}
          {locBoxes(invite.locationIds, (locationIds) => setInvite({ ...invite, locationIds }))}
        </Modal>
      )}
    </div>
  );
}

export function ContractsPage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const { query } = useLocation();
  const tab = query.get('tab') ?? 'contracts';
  const [person, setPerson] = useState<{ id: string; label: string } | null>(null);
  const [key, setKey] = useState(0);
  return (
    <div className="page">
      <PageHeader title={i18n.t('nav.contracts')} />
      <Tabs value={tab} onChange={(v) => setQuery({ tab: v })} tabs={[
        { id: 'contracts', label: i18n.t('contracts.all') },
        { id: 'new', label: i18n.t('contracts.new') },
        { id: 'templates', label: i18n.t('contracts.templates') },
      ]} />
      {tab === 'contracts' && (
        <ResourcePage key={key} slug={gym.slug} resource="contracts" columns={['title', 'person_id', 'contract_type', 'status', 'signed_at']}
          rowActions={(c, reload) => <ContractsInlineActions c={c} reload={reload} />} />
      )}
      {tab === 'new' && (
        <Card>
          <Field label={i18n.t('contracts.person')}><PersonPicker slug={gym.slug} value={person} onChange={setPerson} /></Field>
          <ContractGenerator personId={person?.id} onCreated={() => { setKey((k) => k + 1); setQuery({ tab: 'contracts' }); }} />
        </Card>
      )}
      {tab === 'templates' && (
        <>
          <p className="small muted">{i18n.t('contracts.templates_help')}</p>
          <ResourcePage slug={gym.slug} resource="contract_templates" columns={['name', 'category', 'contract_type', 'locale', 'version']}
            rowActions={(t, reload) => (!t.tenant_id ? (
              <AsyncButton size="sm" variant="secondary" onClick={async () => { await api(`${T(gym.slug)}/admin/contract-templates/${t.id}/copy`, { body: {} }); toast.ok(i18n.t('contracts.copied')); reload(); }}>{i18n.t('contracts.copy')}</AsyncButton>
            ) : null)} />
        </>
      )}
    </div>
  );
}

function ContractsInlineActions({ c, reload }: { c: any; reload(): void }) {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => openDocument(`${T(gym.slug)}/admin/contracts/${c.id}/document`)}>{i18n.t('common.view')}</Button>
      {['draft', 'sent', 'viewed'].includes(c.status) && <AsyncButton size="sm" variant="secondary" onClick={async () => { await api(`${T(gym.slug)}/admin/contracts/${c.id}/send`, { body: {} }); toast.ok(i18n.t('contracts.sent')); reload(); }}>{i18n.t(c.status === 'draft' ? 'contracts.send' : 'contracts.resend')}</AsyncButton>}
      {c.status === 'signed' && !c.countersigned_at && hasRole(gym.membership, 'owner', 'manager') && (
        <AsyncButton size="sm" onClick={async () => { await api(`${T(gym.slug)}/admin/contracts/${c.id}/countersign`, { body: {} }); reload(); }}>{i18n.t('contracts.countersign')}</AsyncButton>
      )}
    </>
  );
}
